import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { categoriesApi, merchantPatternsApi, MerchantPattern, PatternTestResult } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Plus, Pencil, Trash2, PlayCircle, Wand2 } from 'lucide-react'
import { CategoryIcon } from '@/lib/category-icon'
import { Category } from '@/types'

function formatRange(p: MerchantPattern): string {
  if (p.min_amount == null && p.max_amount == null) return 'any'
  if (p.min_amount != null && p.max_amount != null) return `$${p.min_amount} – $${p.max_amount}`
  if (p.min_amount != null) return `≥ $${p.min_amount}`
  return `≤ $${p.max_amount}`
}

interface CategoryChipProps {
  icon?: string | null
  color?: string | null
  name: string
}

function CategoryChip({ icon, color, name }: CategoryChipProps) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium"
      style={{
        backgroundColor: color ? `${color}20` : undefined,
        color: color || undefined,
        border: color ? `1px solid ${color}40` : '1px solid var(--border)',
      }}
    >
      <CategoryIcon name={icon} />
      <span>{name}</span>
    </span>
  )
}

interface FormState {
  pattern: string
  category_id: string
  min_amount: string
  max_amount: string
}

const emptyForm: FormState = {
  pattern: '',
  category_id: '',
  min_amount: '',
  max_amount: '',
}

export default function Patterns() {
  const queryClient = useQueryClient()

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingPattern, setEditingPattern] = useState<MerchantPattern | null>(null)
  const [form, setForm] = useState<FormState>(emptyForm)
  const [formError, setFormError] = useState<string | null>(null)
  const [pageError, setPageError] = useState<string | null>(null)
  const [pageMessage, setPageMessage] = useState<string | null>(null)

  const [testDescription, setTestDescription] = useState('')
  const [testAmount, setTestAmount] = useState('')
  const [testResult, setTestResult] = useState<PatternTestResult | null>(null)
  const [testError, setTestError] = useState<string | null>(null)

  const { data: patterns = [], isLoading } = useQuery<MerchantPattern[]>({
    queryKey: ['merchant-patterns'],
    queryFn: merchantPatternsApi.list,
  })

  const { data: allCategories = [] } = useQuery<Category[]>({
    queryKey: ['categories', 'all'],
    queryFn: async () => {
      const res = await categoriesApi.getAll()
      return res.data
    },
  })

  const createMutation = useMutation({
    mutationFn: (data: { category_id: number; pattern: string; min_amount?: number | null; max_amount?: number | null }) =>
      merchantPatternsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchant-patterns'] })
      closeDialog()
      setPageMessage('Pattern created')
      setPageError(null)
    },
    onError: (err: any) => {
      setFormError(err?.response?.data?.error || 'Failed to create pattern')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<{ category_id: number; pattern: string; min_amount: number | null; max_amount: number | null }> }) =>
      merchantPatternsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchant-patterns'] })
      closeDialog()
      setPageMessage('Pattern updated')
      setPageError(null)
    },
    onError: (err: any) => {
      setFormError(err?.response?.data?.error || 'Failed to update pattern')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => merchantPatternsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchant-patterns'] })
      setPageMessage('Pattern deleted')
      setPageError(null)
    },
    onError: (err: any) => {
      setPageError(err?.response?.data?.error || 'Failed to delete pattern')
      setPageMessage(null)
    },
  })

  const backfillMutation = useMutation({
    mutationFn: (id: number) => merchantPatternsApi.backfill(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['spending-summary'] })
      setPageMessage(`Applied to ${data.updated} transaction${data.updated === 1 ? '' : 's'}`)
      setPageError(null)
    },
    onError: (err: any) => {
      setPageError(err?.response?.data?.error || 'Failed to apply pattern')
      setPageMessage(null)
    },
  })

  function openCreateDialog() {
    setEditingPattern(null)
    setForm(emptyForm)
    setFormError(null)
    setIsDialogOpen(true)
  }

  function openEditDialog(p: MerchantPattern) {
    setEditingPattern(p)
    setForm({
      pattern: p.pattern,
      category_id: String(p.category_id),
      min_amount: p.min_amount != null ? String(p.min_amount) : '',
      max_amount: p.max_amount != null ? String(p.max_amount) : '',
    })
    setFormError(null)
    setIsDialogOpen(true)
  }

  function closeDialog() {
    setIsDialogOpen(false)
    setEditingPattern(null)
    setForm(emptyForm)
    setFormError(null)
  }

  function handleSave() {
    setFormError(null)
    if (!form.pattern.trim()) {
      setFormError('Pattern is required')
      return
    }
    if (!form.category_id) {
      setFormError('Category is required')
      return
    }
    const min = form.min_amount.trim() === '' ? null : Number(form.min_amount)
    const max = form.max_amount.trim() === '' ? null : Number(form.max_amount)
    if (min != null && Number.isNaN(min)) {
      setFormError('Min amount must be a number')
      return
    }
    if (max != null && Number.isNaN(max)) {
      setFormError('Max amount must be a number')
      return
    }
    if (min != null && max != null && min > max) {
      setFormError('Min amount cannot be greater than max')
      return
    }

    const data = {
      pattern: form.pattern.trim(),
      category_id: parseInt(form.category_id),
      min_amount: min,
      max_amount: max,
    }

    if (editingPattern) {
      updateMutation.mutate({ id: editingPattern.id, data })
    } else {
      createMutation.mutate(data)
    }
  }

  function handleDelete(p: MerchantPattern) {
    if (!confirm(`Delete pattern "${p.pattern}"?`)) return
    deleteMutation.mutate(p.id)
  }

  function handleBackfill(p: MerchantPattern) {
    backfillMutation.mutate(p.id)
  }

  async function handleTest() {
    setTestError(null)
    setTestResult(null)
    if (!testDescription.trim()) {
      setTestError('Description is required')
      return
    }
    const amountNum = testAmount.trim() === '' ? undefined : Number(testAmount)
    if (amountNum != null && Number.isNaN(amountNum)) {
      setTestError('Amount must be a number')
      return
    }
    try {
      const result = await merchantPatternsApi.test(testDescription.trim(), amountNum)
      setTestResult(result)
    } catch (err: any) {
      setTestError(err?.response?.data?.error || 'Test failed')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Patterns</h1>
          <p className="text-muted-foreground">
            Automatically categorize transactions by description and amount
          </p>
        </div>
        <Button onClick={openCreateDialog}>
          <Plus className="h-4 w-4 mr-2" />
          New Pattern
        </Button>
      </div>

      {pageError && (
        <Alert variant="destructive">
          <AlertDescription>{pageError}</AlertDescription>
        </Alert>
      )}
      {pageMessage && (
        <Alert>
          <AlertDescription>{pageMessage}</AlertDescription>
        </Alert>
      )}

      {/* Test panel */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <PlayCircle className="h-5 w-5" />
            Test a description
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-[1fr_160px_auto]">
            <div className="space-y-1.5">
              <Label htmlFor="test-description">Description</Label>
              <Input
                id="test-description"
                placeholder="e.g. STARBUCKS #1234"
                value={testDescription}
                onChange={(e) => setTestDescription(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="test-amount">Amount (optional)</Label>
              <Input
                id="test-amount"
                type="number"
                step="0.01"
                placeholder="any"
                value={testAmount}
                onChange={(e) => setTestAmount(e.target.value)}
              />
            </div>
            <div className="flex items-end">
              <Button onClick={handleTest} className="w-full sm:w-auto">
                Test
              </Button>
            </div>
          </div>
          {testError && (
            <Alert variant="destructive">
              <AlertDescription>{testError}</AlertDescription>
            </Alert>
          )}
          {testResult && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Result:</span>
              {testResult.match ? (
                <CategoryChip
                  icon={testResult.match.category_icon}
                  color={testResult.match.category_color}
                  name={testResult.match.category_name}
                />
              ) : (
                <span className="text-muted-foreground italic">No match</span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Patterns list */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">All Patterns</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Loading...</div>
          ) : patterns.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">
              No patterns yet. Create one to start auto-categorizing transactions.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">Pattern</th>
                    <th className="py-2 pr-4 font-medium">Category</th>
                    <th className="py-2 pr-4 font-medium">Amount Range</th>
                    <th className="py-2 pr-4 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {patterns.map((p) => {
                    const isGlobal = p.household_id == null
                    return (
                    <tr key={p.id} className="border-b last:border-b-0">
                      <td className="py-3 pr-4 font-medium">
                        <span className="inline-flex items-center gap-2">
                          {p.pattern}
                          {isGlobal && (
                            <span
                              className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-muted text-muted-foreground border"
                              title="Built-in pattern (read-only)"
                            >
                              built-in
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        <CategoryChip
                          icon={p.category_icon}
                          color={p.category_color}
                          name={p.category_name}
                        />
                      </td>
                      <td className="py-3 pr-4 text-muted-foreground">{formatRange(p)}</td>
                      <td className="py-3 pr-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleBackfill(p)}
                            disabled={backfillMutation.isPending}
                            title="Apply to uncategorized transactions"
                          >
                            <Wand2 className="h-4 w-4 mr-1" />
                            Apply
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(p)}
                            disabled={isGlobal}
                            title={isGlobal ? 'Built-in patterns cannot be edited' : 'Edit'}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(p)}
                            disabled={deleteMutation.isPending || isGlobal}
                            title={isGlobal ? 'Built-in patterns cannot be deleted' : 'Delete'}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => (open ? setIsDialogOpen(true) : closeDialog())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingPattern ? 'Edit Pattern' : 'New Pattern'}</DialogTitle>
            <DialogDescription>
              Patterns match transaction descriptions (case-insensitive substring). Optional amount
              bounds let you scope a pattern to specific dollar ranges.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="pattern">Pattern</Label>
              <Input
                id="pattern"
                placeholder="e.g. STARBUCKS"
                value={form.pattern}
                onChange={(e) => setForm({ ...form, pattern: e.target.value })}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="category">Category</Label>
              <Select
                value={form.category_id}
                onValueChange={(value) => setForm({ ...form, category_id: value })}
              >
                <SelectTrigger id="category">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {allCategories.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      <span className="inline-flex items-center gap-1.5">
                        <CategoryIcon name={c.icon} />
                        {c.name}
                        <span className="text-muted-foreground text-xs ml-1">({c.type})</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="min-amount">Min Amount</Label>
                <Input
                  id="min-amount"
                  type="number"
                  step="0.01"
                  placeholder="any"
                  value={form.min_amount}
                  onChange={(e) => setForm({ ...form, min_amount: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="max-amount">Max Amount</Label>
                <Input
                  id="max-amount"
                  type="number"
                  step="0.01"
                  placeholder="any"
                  value={form.max_amount}
                  onChange={(e) => setForm({ ...form, max_amount: e.target.value })}
                />
              </div>
            </div>

            {formError && (
              <Alert variant="destructive">
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {editingPattern ? 'Save Changes' : 'Create Pattern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
